import { z } from "zod";
import { InvalidWhatsAppIdError } from "../errors/invalid-whatsapp-id.error.js";
import { E164PhoneNumber } from "./phone.schemas.js";

export const WhatsAppIdSchema = z
  .string()
  .refine((val) => isValidWhatsAppId(val), {
    message: "Invalid WhatsApp ID format",
  })
  .brand("WhatsAppId");

export type WhatsAppId = z.infer<typeof WhatsAppIdSchema>;

const WHATSAPP_ID_REGEX = /^[1-9]\d{6,14}$/;

function normalizeWhatsAppId(value: string): string {
  return value.replace(/[\s\-().+]/g, "");
}

function isValidWhatsAppId(value: string): boolean {
  return typeof value === "string" && WHATSAPP_ID_REGEX.test(value);
}

export const WhatsAppId = {
  /**
   * Create a WhatsAppId from a string, with normalization and validation.
   * Normalizes the input (removes +, spaces, dashes, etc.) then validates the result is a valid WhatsApp ID.
   * @param value - String to convert (can be E.164 format or raw wa_id)
   * @returns WhatsAppId branded value
   * @throws InvalidWhatsAppIdError if value cannot be normalized to valid format
   * @example
   * WhatsAppId.create('14155551234')     // Returns '14155551234'
   * WhatsAppId.create('+14155551234')    // Returns '14155551234'
   * WhatsAppId.create('+1 415 555 1234') // Returns '14155551234'
   */
  create(value: string): WhatsAppId {
    const normalized = normalizeWhatsAppId(value);
    if (!isValidWhatsAppId(normalized)) {
      throw new InvalidWhatsAppIdError(value);
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalized as WhatsAppId;
  },

  unsafeCreate(value: string): WhatsAppId {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return value as WhatsAppId;
  },

  isValid(value: string): boolean {
    return isValidWhatsAppId(value);
  },

  isWhatsAppId(value: string): value is WhatsAppId {
    return isValidWhatsAppId(value);
  },

  tryCreate(value: string): WhatsAppId | undefined {
    const normalized = normalizeWhatsAppId(value);
    if (!isValidWhatsAppId(normalized)) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return normalized as WhatsAppId;
  },

  /**
   * Try to convert a WhatsApp ID to E.164 phone number format.
   * Legacy helper — assumes wa_id is a phone number (true today, may break when Meta introduces non-phone identifiers).
   * @param waId - WhatsApp ID
   * @returns E.164 phone number, or undefined if conversion fails
   */
  tryToE164(waId: WhatsAppId): E164PhoneNumber | undefined {
    return E164PhoneNumber.tryCreate(`+${waId}`);
  },
} as const;
